import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Pencil, X } from 'lucide-react';

import { assistantsService } from '../../services/api/assistants';

interface Props {
  conversationId: number;
  titre: string;
  onRenomme?: (titre: string) => void;
}

/**
 * Intitulé d'un échange, modifiable sur place.
 *
 * À l'ouverture, un échange reprend le nom de son assistant. Dès qu'on s'en
 * sert vraiment, « Mes conversations » aligne donc dix « Budgétisation et
 * quantification » qu'il faut ouvrir un par un pour les distinguer.
 *
 * Le renommer est une correction de tri, pas une action à part : elle se fait
 * là où le titre s'affiche, sans quitter l'échange.
 */
const TitreConversation: React.FC<Props> = ({ conversationId, titre, onRenomme }) => {
  const [edition, setEdition] = useState(false);
  const [valeur, setValeur] = useState(titre);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');
  const champ = useRef<HTMLInputElement>(null);

  useEffect(() => setValeur(titre), [titre]);

  useEffect(() => {
    if (edition) {
      champ.current?.focus();
      champ.current?.select();
    }
  }, [edition]);

  const abandonner = () => {
    setValeur(titre);
    setErreur('');
    setEdition(false);
  };

  const enregistrer = async () => {
    const nouveau = valeur.trim();
    if (!nouveau || nouveau === titre) {
      abandonner();
      return;
    }

    setOccupe(true);
    setErreur('');
    try {
      const reponse = await assistantsService.renommerConversation(conversationId, nouveau);
      onRenomme?.(reponse.title);
      setEdition(false);
    } catch (e: any) {
      setErreur(e.message || 'Renommage impossible.');
    } finally {
      setOccupe(false);
    }
  };

  if (!edition) {
    return (
      <div className="group flex min-w-0 items-center gap-1.5">
        <h1 className="truncate text-base font-semibold text-eg-encre">{titre}</h1>
        <button
          type="button"
          onClick={() => setEdition(true)}
          title="Renommer cet échange"
          aria-label="Renommer cet échange"
          className="shrink-0 rounded p-1 text-eg-bord transition-colors
            hover:bg-eg-voile hover:text-eg-brun group-hover:text-eg-estompe"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <input
          ref={champ}
          value={valeur}
          onChange={(e) => setValeur(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') enregistrer();
            if (e.key === 'Escape') abandonner();
          }}
          onBlur={enregistrer}
          disabled={occupe}
          maxLength={255}
          className="min-w-0 flex-1 rounded-md border border-eg-accent bg-eg-blanc px-2 py-1
            text-base font-semibold text-eg-encre focus:outline-none disabled:opacity-50"
        />
        <button
          type="button"
          // onMouseDown plutôt que onClick : le clic ferait d'abord perdre le
          // focus au champ, et le bouton disparaîtrait avant d'être atteint.
          onMouseDown={(e) => e.preventDefault()}
          onClick={enregistrer}
          disabled={occupe}
          title="Enregistrer"
          className="shrink-0 rounded p-1 text-eg-valide hover:bg-eg-valide/10 disabled:opacity-50"
        >
          {occupe ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={abandonner}
          disabled={occupe}
          title="Annuler"
          className="shrink-0 rounded p-1 text-eg-gris hover:bg-eg-voile disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {erreur && <p className="mt-1 text-xs text-eg-accent">{erreur}</p>}
    </div>
  );
};

export default TitreConversation;
